/**
 * What a reviewer reads in an extractor draft before approving it (F4), kept pure so it is tested without a browser.
 * The draft's JSON comes from the brain and the runner (import_extractor_versions: manifest, selftest, validation,
 * author_notes), so every field is read defensively: any may be missing, an older draft lacks the newer notes, and a
 * self-test shrunk to fit its cap lacks parts of its report.
 *
 *   • draftSummary: the counts in all and per stream, each stream's preview rows, and what was skipped or set aside,
 *     from the cloud's dry run on the file (validation), else from the brain's self-test;
 *   • coverageOf: every source column and where it goes, from the author's placements, else from the manifest;
 *   • questionGroups: the author's questions by priority, in four parts or as one sentence;
 *   • wordRows / wordBindings: the defect words the draft proposes, the reviewer's choice for each, and the body of
 *     PUT /v1/twin/vocabulary-bindings that saves them;
 *   • approveWithWords: the approval, then the words. A failure of the words is said, and never undoes the approval.
 */
import type { ImportExtractorVersion } from "@/lib/api/imports";
import type { VocabBinding, VocabBindingInput, VocabDefectTarget } from "@/lib/api/twin-vocabulary";
import { errorCodeOf, errorDetailOf, isNetworkError, isUnauthorized, statusOf } from "@/lib/api/error";
import { defectLabel } from "@/lib/lot-words";
import { sameSpelling, spellingForm } from "./defect-spelling";

/** A JSON object as stored, every value still to be checked. */
export type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const obj = (v: unknown): Obj => (isObj(v) ? v : {});
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
/** A string as written (a column's name keeps its spaces), or "". */
const str = (v: unknown): string => (typeof v === "string" ? v : "");
/** A string trimmed, or "". */
const text = (v: unknown): string => str(v).trim();
/** A finite number, or null. */
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** A count as the page writes it: 1,240. */
export function fmtCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** A count with its noun: "1 row", "1,240 rows". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${fmtCount(n)} ${n === 1 ? one : many}`;
}

/** The sum of the numbers given, or null when none is. */
function sumOf(values: readonly (number | null)[]): number | null {
  let out: number | null = null;
  for (const v of values) if (v !== null) out = (out ?? 0) + v;
  return out;
}

/** A name as the correlation engine reads it (vocab_bindings.go vocabKey): lower case, spaces and hyphens folded to
 *  underscores, runs of underscores collapsed. "Sand Inclusion" and "sand-inclusion" are one word. */
export function vocabKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[- ]/g, "_").replace(/_{2,}/g, "_");
}

// ── The manifest ─────────────────────────────────────────────────────────────────────────────────────────────

export interface DraftParam {
  /** The source column, as the file names it. */
  source: string;
  tag: string;
  unit: string;
  equipment: string;
}

export interface DraftStream {
  key: string;
  mode: string;
  kind: string;
  equipment: string;
  params: DraftParam[];
  /** Source columns the time is read from, and the records' other fields (heat, part, counts), for coverage. */
  timeCols: string[];
  identityCols: string[];
}

/** The manifest's streams (forge-extract-manifest/v1), each with a key, in the manifest's order. */
export function manifestStreams(manifest: unknown): DraftStream[] {
  const out: DraftStream[] = [];
  for (const raw of list(obj(manifest).streams)) {
    const s = obj(raw);
    const key = text(s.key);
    if (!key) continue;
    const params: DraftParam[] = [];
    for (const p of list(s.params)) {
      const o = obj(p);
      const tag = text(o.tag);
      if (tag) params.push({ source: str(o.source), tag, unit: text(o.unit), equipment: text(o.equipment) });
    }
    const columns = (v: unknown) => list(v).map(str).filter((c) => c.trim() !== "");
    out.push({
      key,
      mode: text(s.mode),
      kind: text(s.kind),
      equipment: text(s.equipment),
      params,
      timeCols: columns(s.time_cols),
      identityCols: columns(s.identity_cols),
    });
  }
  return out;
}

// ── The counts ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface StreamSummary {
  key: string;
  mode: string;
  kind: string;
  params: DraftParam[];
  /** Records the reader made of the file for this stream (the harness's stats.records). */
  read: number | null;
  /** Rows that import after the checks (the preview's rows, else stats.rows). */
  rows: number | null;
  /** Production days the rows fall on, and the first and last (YYYY-MM-DD, "" when unknown). */
  days: number | null;
  firstDay: string;
  lastDay: string;
  /** Values and rows set aside with a reason: quarantined, an implausible date, a lot held. */
  setAside: number | null;
  /** A lots stream's lots written, and held. */
  lots: number | null;
  lotsHeld: number | null;
  /** Products, when the run counted them. */
  products: number | null;
  /** The first rows that import, as the import receives them. */
  sample: Obj[];
}

export interface SkipCount {
  stream: string;
  reason: string;
  count: number;
}

export interface QuarantineItem {
  stream: string;
  row: number | null;
  column: string;
  value: string;
  reason: string;
}

export interface DraftSummary {
  /** Which run the figures come from: the cloud's dry run on the file, the brain's self-test, or neither. */
  from: "dry run" | "self-test" | null;
  streams: StreamSummary[];
  /** The rows of the file the run read, when it counted them for the whole file (rows_read); never a sum of the
   *  streams' records, since streams that read the same rows would count them twice. Each stream says its own. */
  rowsRead: number | null;
  toImport: number | null;
  /** Rows the reader skipped on purpose, in all (null without a report). */
  skippedTotal: number | null;
  skipped: SkipCount[];
  setAside: number | null;
  /** The first of the items set aside, with their reasons (the report keeps a sample). */
  quarantineSample: QuarantineItem[];
  lots: number | null;
  lotsHeld: number | null;
  products: number | null;
  firstDay: string;
  lastDay: string;
  /** What the checks left out besides, in words ("40 blank cells"). */
  dropped: string[];
}

/** What the checks leave out without setting it aside, by the harness's stat. */
const DROPPED: readonly (readonly [stat: string, one: string, many: string])[] = [
  ["blank", "blank cell", "blank cells"],
  ["zeros_to_missing", "zero read as no reading", "zeros read as no reading"],
  ["stuck_to_missing", "stuck value read as no reading", "stuck values read as no reading"],
  ["empty_after_gate", "row left with no value", "rows left with no value"],
  ["idle_repeats_dropped", "idle repeat dropped", "idle repeats dropped"],
  ["channels_skipped", "dead or frozen channel not imported", "dead or frozen channels not imported"],
];

/** The run whose figures a reviewer reads: each part from the dry run on the file, else from the self-test. An empty
 *  preview or report (the sandbox's answer for a run that never read the file) is absent: it counts nothing. */
function runDocs(version: ImportExtractorVersion): { preview: Obj | null; report: Obj | null; from: DraftSummary["from"] } {
  const v = obj(version.validation);
  const s = obj(version.selftest);
  const pick = (x: unknown): Obj | null => (isObj(x) && Object.keys(x).length > 0 ? x : null);
  const vp = pick(v.preview);
  const vr = pick(v.report);
  const sp = pick(s.preview);
  const sr = pick(s.report);
  return { preview: vp ?? sp, report: vr ?? sr, from: vp || vr ? "dry run" : sp || sr ? "self-test" : null };
}

/** Which run the preview (the counts per stream) and the report (set aside, skipped) each come from: the summary's
 *  `from` says "dry run" when either half does, so two summaries compare like with like only when both halves do. */
export function runSources(version: ImportExtractorVersion): {
  preview: "dry run" | "self-test" | null;
  report: "dry run" | "self-test" | null;
} {
  const v = obj(version.validation);
  const s = obj(version.selftest);
  const has = (x: unknown) => isObj(x) && Object.keys(x).length > 0;
  return {
    preview: has(v.preview) ? "dry run" : has(s.preview) ? "self-test" : null,
    report: has(v.report) ? "dry run" : has(s.report) ? "self-test" : null,
  };
}

function quarantineItems(v: unknown): QuarantineItem[] {
  const out: QuarantineItem[] = [];
  for (const raw of list(v)) {
    const o = obj(raw);
    const value = o.value === null || o.value === undefined ? "" : typeof o.value === "string" ? o.value : JSON.stringify(o.value);
    const reason = text(o.reason);
    if (!reason && !value) continue;
    out.push({ stream: text(o.stream), row: num(o.row), column: str(o.column), value, reason });
  }
  return out;
}

/** The counts a reviewer reads, in all and per stream (the manifest's streams first, in its order). */
export function draftSummary(version: ImportExtractorVersion): DraftSummary {
  const { preview, report, from } = runDocs(version);
  const manifest = manifestStreams(version.manifest);
  const previewStreams = obj(preview?.streams);
  const stats = obj(report?.stats);
  const keys = [...manifest.map((s) => s.key)];
  for (const k of [...Object.keys(previewStreams), ...Object.keys(stats)]) if (!keys.includes(k)) keys.push(k);

  const streams = keys.map((key): StreamSummary => {
    const m = manifest.find((s) => s.key === key);
    const ps = obj(previewStreams[key]);
    const st = obj(stats[key]);
    const mode = m?.mode ?? "";
    const lotsWritten = num(st.lots_written);
    const isLots = mode === "lots" || (!m && (lotsWritten ?? 0) > 0);
    return {
      key,
      mode,
      kind: m?.kind ?? "",
      params: m?.params ?? [],
      read: num(st.records),
      rows: num(ps.rows) ?? num(st.rows),
      days: num(ps.days),
      firstDay: text(ps.first_day),
      lastDay: text(ps.last_day),
      setAside: sumOf([num(st.quarantined), num(st.implausible_dates), num(st.lots_held)]),
      lots: isLots ? lotsWritten : null,
      lotsHeld: isLots ? num(st.lots_held) : null,
      products: num(ps.products) ?? num(st.products),
      sample: list(ps.sample).filter(isObj),
    };
  });

  const skipped: SkipCount[] = [];
  for (const raw of list(report?.skipped)) {
    const o = obj(raw);
    const count = num(o.count);
    if (count !== null && count > 0) skipped.push({ stream: text(o.stream), reason: text(o.reason) || "no reason", count });
  }
  const skippedTotal = report ? skipped.reduce((n, k) => n + k.count, 0) : null;
  const days = streams.flatMap((s) => [s.firstDay, s.lastDay]).filter(Boolean).sort();
  const perStreamProducts = streams.map((s) => s.products).filter((p): p is number => p !== null);
  const reportProducts = report?.products;
  const dropped: string[] = [];
  for (const [stat, one, many] of DROPPED) {
    const n = sumOf(streams.map((s) => num(obj(stats[s.key])[stat]))) ?? 0;
    if (n > 0) dropped.push(plural(n, one, many));
  }
  return {
    from,
    streams,
    rowsRead: num(report?.rows_read) ?? num(preview?.rows_read),
    toImport: sumOf(streams.map((s) => s.rows)),
    skippedTotal,
    skipped,
    setAside: num(preview?.quarantine_count) ?? sumOf(streams.map((s) => s.setAside)),
    quarantineSample: quarantineItems(report?.quarantine_sample),
    lots: sumOf(streams.map((s) => s.lots)),
    lotsHeld: sumOf(streams.map((s) => s.lotsHeld)),
    products:
      num(reportProducts) ??
      (Array.isArray(reportProducts) ? reportProducts.length : perStreamProducts.length === 1 ? perStreamProducts[0] : null),
    firstDay: days[0] ?? "",
    lastDay: days[days.length - 1] ?? "",
    dropped,
  };
}

export interface Fact {
  label: string;
  value: string;
}

/** The draft's counts in all, as label and value, in the order a reviewer reads them. */
export function summaryFacts(s: DraftSummary): Fact[] {
  const out: Fact[] = [];
  if (s.rowsRead !== null) out.push({ label: "Rows read", value: fmtCount(s.rowsRead) });
  if (s.toImport !== null) out.push({ label: "Rows to import", value: fmtCount(s.toImport) });
  if (s.skippedTotal !== null) out.push({ label: "Skipped on purpose", value: fmtCount(s.skippedTotal) });
  if (s.setAside !== null) out.push({ label: "Set aside", value: fmtCount(s.setAside) });
  if (s.lots !== null) {
    out.push({ label: "Lots", value: s.lotsHeld ? `${fmtCount(s.lots)} (${fmtCount(s.lotsHeld)} held)` : fmtCount(s.lots) });
  }
  if (s.products !== null) out.push({ label: "Products", value: fmtCount(s.products) });
  if (s.firstDay) {
    const one = !s.lastDay || s.firstDay === s.lastDay;
    out.push({ label: one ? "Day" : "Days", value: one ? s.firstDay : `${s.firstDay} to ${s.lastDay}` });
  }
  return out;
}

/** One stream's counts in a line: "620 records read · 600 rows to import · 31 days, 2026-08-01 to 2026-08-31". */
export function streamLine(s: StreamSummary): string {
  const parts: string[] = [];
  if (s.read !== null) parts.push(`${plural(s.read, "record")} read`);
  if (s.rows !== null) parts.push(`${plural(s.rows, s.mode === "lots" ? "lot" : "row")} to import`);
  if (s.days !== null && s.days > 0) {
    const span =
      s.firstDay && s.lastDay && s.firstDay !== s.lastDay ? `, ${s.firstDay} to ${s.lastDay}` : s.firstDay ? `, ${s.firstDay}` : "";
    parts.push(`${plural(s.days, "day")}${span}`);
  }
  if (s.setAside) parts.push(`${fmtCount(s.setAside)} set aside`);
  if (s.products !== null) parts.push(plural(s.products, "product"));
  return parts.join(" · ");
}

const LEAD_COLUMNS = ["time", "heat", "ladle", "part", "grade", "cast", "scrap", "heats"];

/** A preview's columns: the time and the records' fields first, then each value's key as the rows first carry it. */
export function sampleColumns(sample: readonly Obj[]): string[] {
  const seen = new Set<string>();
  const rest: string[] = [];
  for (const row of sample) {
    for (const k of Object.keys(row)) {
      if (seen.has(k)) continue;
      seen.add(k);
      if (!LEAD_COLUMNS.includes(k)) rest.push(k);
    }
  }
  return [...LEAD_COLUMNS.filter((k) => seen.has(k)), ...rest];
}

/** A preview cell as text: a number as the import receives it, a long string cut. */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (typeof v === "boolean") return v ? "true" : "false";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 40 ? `${s.slice(0, 39)}…` : s;
}

/** The reasons of the items set aside, most first. */
export function reasonTally(items: readonly QuarantineItem[]): { reason: string; count: number }[] {
  const by = new Map<string, number>();
  for (const q of items) by.set(q.reason || "no reason", (by.get(q.reason || "no reason") ?? 0) + 1);
  return [...by.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

// ── The self-test ────────────────────────────────────────────────────────────────────────────────────────────────

export interface Finding {
  rule: string;
  /** "stream.tag", the stream, or "". */
  where: string;
  detail: string;
}

export interface SelftestSummary {
  present: boolean;
  ok: boolean | null;
  rounds: number | null;
  /** The brain shortened it to fit its cap: some figures are left out. */
  truncated: boolean;
  warnings: Finding[];
  held: Finding[];
}

function findings(v: unknown): Finding[] {
  const out: Finding[] = [];
  for (const raw of list(v)) {
    if (typeof raw === "string") {
      if (raw.trim()) out.push({ rule: "", where: "", detail: raw.trim() });
      continue;
    }
    const o = obj(raw);
    const rule = text(o.rule);
    const detail = text(o.detail);
    if (!rule && !detail) continue;
    const stream = text(o.stream);
    const tag = text(o.tag);
    out.push({ rule, where: stream && tag ? `${stream}.${tag}` : stream || tag, detail });
  }
  return out;
}

/** The brain's self-test of the draft: whether it passed, in how many rounds, and what it flagged or held. */
export function selftestOf(version: ImportExtractorVersion): SelftestSummary {
  const raw: unknown = version.selftest;
  const s = obj(raw);
  return {
    present: isObj(raw),
    ok: typeof s.ok === "boolean" ? s.ok : null,
    rounds: num(s.rounds),
    truncated: s.truncated === true,
    warnings: findings(s.warnings),
    held: findings(s.held),
  };
}

/** The self-test in a line: "passed after 2 rounds, 3 warnings". */
export function selftestText(st: SelftestSummary): string {
  if (!st.present) return "none stored";
  const parts = [
    st.ok === true
      ? `passed${st.rounds ? ` after ${plural(st.rounds, "round")}` : ""}`
      : st.ok === false
        ? "not passed"
        : "stored",
  ];
  if (st.warnings.length) parts.push(plural(st.warnings.length, "warning"));
  if (st.held.length) parts.push(`${fmtCount(st.held.length)} held for review`);
  if (st.truncated) parts.push("shortened to fit, so some figures are left out");
  return parts.join(", ");
}

// ── Where each column goes ─────────────────────────────────────────────────────────────────────────────────────

export interface CoverageRow {
  /** The sheet, when the author named it. */
  sheet: string;
  column: string;
  /** Where it goes, in words: "compactibility in muller.log", "lot: part", "not imported"; "" for nowhere. */
  goesTo: string;
  reason: string;
  skipped: boolean;
  /** Why the row needs a look ("no destination"), or "". */
  flag: string;
}

export interface Coverage {
  from: "placements" | "manifest";
  rows: CoverageRow[];
  /** Rows flagged: a column with no destination. */
  flagged: number;
}

const NO_DESTINATION = "no destination";

/** Where a placement's `to` goes: "<stream>.<tag>" (a stream key may carry dots), "lot.<field>", "product.<field>"
 *  or "skip". */
function placementGoesTo(to: string, streams: readonly DraftStream[]): Pick<CoverageRow, "goesTo" | "skipped" | "flag"> {
  if (!to) return { goesTo: "", skipped: false, flag: NO_DESTINATION };
  if (to === "skip") return { goesTo: "not imported", skipped: true, flag: "" };
  const stream = streams
    .map((s) => s.key)
    .filter((k) => to.length > k.length + 1 && to.startsWith(`${k}.`))
    .sort((a, b) => b.length - a.length)[0];
  if (stream) return { goesTo: `${to.slice(stream.length + 1)} in ${stream}`, skipped: false, flag: "" };
  for (const [prefix, word] of [["lot.", "lot"], ["product.", "product"]] as const) {
    if (to.length > prefix.length && to.startsWith(prefix)) {
      return { goesTo: `${word}: ${to.slice(prefix.length)}`, skipped: false, flag: "" };
    }
  }
  return { goesTo: to, skipped: false, flag: "names nothing this reader writes" };
}

/**
 * Every source column and where it goes: from the author's placements when the draft has them, else from the
 * manifest (each stream's time and record columns and params, and the columns it ignores, with why). A column the dry
 * run found with no place (coverage UNMAPPED) is added, and every column with no destination is flagged.
 */
export function coverageOf(version: ImportExtractorVersion): Coverage {
  const streams = manifestStreams(version.manifest);
  const rows: CoverageRow[] = [];
  const seen = new Set<string>();
  const add = (row: CoverageRow) => {
    const k = `${row.sheet}\u0000${row.column}\u0000${row.goesTo}`;
    if (seen.has(k)) return;
    seen.add(k);
    rows.push(row);
  };
  const placements = list(obj(version.author_notes).placements).map(obj).filter((p) => str(p.column).trim() !== "");
  const from: Coverage["from"] = placements.length > 0 ? "placements" : "manifest";
  if (from === "placements") {
    for (const p of placements) {
      add({ sheet: text(p.sheet), column: str(p.column), reason: text(p.reason), ...placementGoesTo(text(p.to), streams) });
    }
  } else {
    for (const s of streams) {
      for (const c of s.timeCols) add({ sheet: "", column: c, goesTo: `the time in ${s.key}`, reason: "", skipped: false, flag: "" });
      for (const c of s.identityCols) {
        add({ sheet: "", column: c, goesTo: `a record field in ${s.key}`, reason: "", skipped: false, flag: "" });
      }
      for (const p of s.params) {
        if (!p.source.trim()) continue;
        const unit = p.unit ? ` (${p.unit})` : "";
        add({ sheet: "", column: p.source, goesTo: `${p.tag}${unit} in ${s.key}`, reason: "", skipped: false, flag: "" });
      }
    }
    for (const [column, why] of Object.entries(obj(obj(version.manifest).ignore))) {
      add({ sheet: "", column, goesTo: "not imported", reason: text(why), skipped: true, flag: "" });
    }
  }
  const listed = new Set(rows.map((r) => r.column));
  for (const doc of [obj(version.validation).report, obj(version.selftest).report]) {
    for (const [column, role] of Object.entries(obj(obj(doc).coverage))) {
      if (role === "UNMAPPED" && !listed.has(column)) {
        listed.add(column);
        add({ sheet: "", column, goesTo: "", reason: "the dry run found no place for it", skipped: false, flag: NO_DESTINATION });
      }
    }
  }
  return { from, rows, flagged: rows.filter((r) => r.flag).length };
}

// ── The author's questions and assumptions ─────────────────────────────────────────────────────────────────────

export type QuestionPriority = "blocks_import" | "blocks_analysis" | "good_to_know";

export interface DraftQuestion {
  /** A four-part question's place in the draft's author_notes.structured_questions, which a send-back's answer names
   *  (plan v21 B5); absent for a question kept only as a line of text. */
  index?: number;
  /** A question asked in one sentence (an older draft's), else "". */
  text: string;
  /** In four parts: what the author saw in the file, what it assumed, and what changes if that is wrong. */
  saw: string;
  assume: string;
  changes: string;
}

export interface QuestionGroup {
  priority: QuestionPriority | null;
  title: string;
  /** What the priority means, or "". */
  hint: string;
  questions: DraftQuestion[];
}

const PRIORITIES: readonly { priority: QuestionPriority; title: string; hint: string }[] = [
  { priority: "blocks_import", title: "Blocks the import", hint: "the file is not read right until this is answered" },
  {
    priority: "blocks_analysis",
    title: "Blocks the analysis",
    hint: "the data imports, but the analysis cannot use it until this is answered",
  },
  { priority: "good_to_know", title: "Good to know", hint: "" },
];

const isPriority = (p: string): p is QuestionPriority => PRIORITIES.some((x) => x.priority === p);

interface ReadQuestion {
  q: DraftQuestion;
  priority: QuestionPriority | null;
  /** Asked as one sentence: a string entry. */
  sentence: boolean;
}

/** The questions a notes list holds: sentences, and four-part objects (with their priority); nothing that says
 *  nothing. */
function readQuestions(v: unknown, indexed = false): ReadQuestion[] {
  const out: ReadQuestion[] = [];
  for (const [i, raw] of list(v).entries()) {
    if (typeof raw === "string") {
      if (raw.trim()) out.push({ q: { text: raw.trim(), saw: "", assume: "", changes: "" }, priority: null, sentence: true });
      continue;
    }
    const o = obj(raw);
    const q: DraftQuestion = {
      text: text(o.question) || text(o.text),
      saw: text(o.saw),
      assume: text(o.assume),
      changes: text(o.changes),
    };
    if (!q.text && !q.saw && !q.assume && !q.changes) continue;
    if (indexed) q.index = i;
    const p = text(o.priority);
    out.push({ q, priority: isPriority(p) ? p : null, sentence: false });
  }
  return out;
}

/** A priority as F3 writes it at the end of a question's line (extract_author.py PRIORITIES). */
const PRIORITY_WORDS: Record<QuestionPriority, string> = {
  blocks_import: "blocks the import",
  blocks_analysis: "blocks an analysis",
  good_to_know: "good to know",
};

/** A line of text as the brain cleans it (extract_author.py clean_line): runs of whitespace as one space, trimmed,
 *  at most 1000 characters. */
function cleanLine(s: string): string {
  return Array.from(s.replace(/\s+/g, " ").trim()).slice(0, 1000).join("");
}

const sentenceOf = (s: string) => (/[.?!]$/.test(s) ? s : `${s}.`);

/** A four-part question as F3 also writes it among the text ones (extract_author.py question_line). */
export function questionLine(q: Pick<DraftQuestion, "saw" | "assume" | "changes">, priority: QuestionPriority): string {
  return cleanLine(
    `${sentenceOf(q.saw)} Until the plant answers: ${sentenceOf(q.assume)} The answer changes: ` +
      `${sentenceOf(q.changes)} (${PRIORITY_WORDS[priority]})`,
  );
}

/**
 * The author's questions by priority, the ones that block the import first; a question with none comes last. F3 keeps
 * `questions` as text (so a page that knows no other form never meets an object): every question as a line, a
 * four-part one in question_line's form, and the four-part ones again under `structured_questions`. Those are shown,
 * and of the text ones only the lines that are not one of them, so no question shows twice and one asked as text alone
 * stays. A four-part object found among the text ones is still read, once. Without four-part questions the text ones
 * read as before.
 */
export function questionGroups(notes: unknown): QuestionGroup[] {
  const n = obj(notes);
  const structured = readQuestions(n.structured_questions, true);
  const repeats = new Set(structured.flatMap((r) => (!r.sentence && r.priority ? [questionLine(r.q, r.priority)] : [])));
  const textual = readQuestions(n.questions).filter((r) => !(r.sentence && repeats.has(cleanLine(r.q.text))));
  const seen = new Set<string>();
  const once = (r: ReadQuestion) => {
    const k = [r.q.text, r.q.saw, r.q.assume, r.q.changes].join("\u0000");
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  };
  const shown = [...structured, ...textual].filter(once);
  const by = new Map<QuestionPriority | null, DraftQuestion[]>();
  for (const r of shown) by.set(r.priority, [...(by.get(r.priority) ?? []), r.q]);
  const groups: QuestionGroup[] = PRIORITIES.filter((p) => by.has(p.priority)).map((p) => ({
    ...p,
    questions: by.get(p.priority) ?? [],
  }));
  const rest = by.get(null);
  if (rest) groups.push({ priority: null, title: groups.length ? "Other questions" : "Questions", hint: "", questions: rest });
  return groups;
}

/** The author's assumptions, each a sentence. */
export function assumptionsOf(notes: unknown): string[] {
  return list(obj(notes).assumptions).map(text).filter(Boolean);
}

// ── The defect words ─────────────────────────────────────────────────────────────────────────────────────────

/** A word of the plant's the draft proposes a meaning for. */
export interface ProposedWord {
  word: string;
  /** The canonical defect proposed, or null: no shared defect matches the word. */
  taxonomyId: string | null;
  note: string;
}

/** The draft's proposed defect words, each once (by its key, as the server keys a binding). */
export function proposedWordsOf(notes: unknown): ProposedWord[] {
  const seen = new Set<string>();
  const out: ProposedWord[] = [];
  for (const raw of list(obj(notes).vocabulary)) {
    const o = obj(raw);
    const word = text(o.word);
    const key = vocabKey(word);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ word, taxonomyId: text(o.taxonomy_id) || null, note: text(o.note) });
  }
  return out;
}

/** The defects a binding may name (GET /v1/twin/vocabulary-bindings/targets), and the fold of a name onto one. */
export interface DefectTargets {
  /** The listed defects, by label. */
  keys: string[];
  /** The listed defect a name folds onto (itself, or through an alias), or null. */
  fold(name: string): string | null;
  /** The org's own defect types among them (migration 0265), each by key onto its name. */
  own: ReadonlyMap<string, string>;
  /** A listed defect in words: one of the org's own types by its name, a pack defect by its key read as words. */
  label(key: string): string;
}

export function defectTargetsOf(targets: readonly VocabDefectTarget[] | null | undefined): DefectTargets | null {
  if (!targets) return null;
  const onto = new Map<string, string>();
  const own = new Map<string, string>();
  const keys: string[] = [];
  for (const t of targets) {
    const key = typeof t?.key === "string" ? t.key : "";
    if (!key || keys.includes(key)) continue;
    keys.push(key);
    onto.set(key, key);
    if (t.own && typeof t.name === "string" && t.name.trim()) own.set(key, t.name.trim());
  }
  for (const t of targets) {
    for (const a of Array.isArray(t?.aliases) ? t.aliases : []) {
      if (typeof a === "string" && !onto.has(vocabKey(a))) onto.set(vocabKey(a), t.key);
    }
  }
  const label = (key: string) => own.get(key) ?? defectLabel(key);
  keys.sort((a, b) => label(a).localeCompare(label(b)));
  return { keys, fold: (name) => onto.get(name) ?? onto.get(vocabKey(name)) ?? null, own, label };
}

/** A listed defect as a select offers it: one of the org's own types is marked as the org's. */
export function defectOptionLabel(key: string, targets: DefectTargets | null): string {
  if (!targets) return defectLabel(key);
  return targets.own.has(key) ? `${targets.label(key)} (the org's own)` : targets.label(key);
}

/**
 * A reviewer's choice for a word: a defect ("defect:<key>"), not a process defect (out_of_scope), not scrap, or later
 * (nothing saved). What each does to the word is the server's (vocab_reads.go): left unbound, the word stays on the
 * outcome axis as the plant's own category; out of scope, it is no process defect (machining, handling, bookkeeping)
 * and leaves every process correlation, still counted in scrap totals and the Paretos; not scrap, its pieces leave every
 * scrap figure.
 */
export type WordChoice = string;
export const LATER: WordChoice = "later";
export const OUT_OF_SCOPE: WordChoice = "out_of_scope";
export const NOT_SCRAP: WordChoice = "not_scrap";
/** A new defect type of the org's own (migration 0265): the select opens a small form, and nothing is saved for the
 *  word until the type is added and the word bound to it. */
export const NEW_TYPE: WordChoice = "new_type";

/** The choice that opens the form for a new defect type, last in a word's select. */
export const NEW_TYPE_OPTION: { value: WordChoice; label: string } = { value: NEW_TYPE, label: "A new defect type…" };

/** The choices that name no defect, as a reviewer's select offers them, each saying what it does to the word. */
export const WORD_CHOICES: readonly { value: WordChoice; label: string }[] = [
  { value: LATER, label: "Decide later (not saved)" },
  {
    value: OUT_OF_SCOPE,
    label:
      "Not a process defect (machining, handling, bookkeeping): keep it in scrap, leave it out of process correlations",
  },
  { value: NOT_SCRAP, label: "Not scrap (a bookkeeping adjustment, a melt entry): its pieces leave every scrap figure" },
];

/**
 * The taxonomy ids of scrap no process explains (machining, finishing, heat treatment). The author may propose them
 * for a word. No correlation group reads them, so a word proposed as one starts at "not a process defect"; they are
 * taxonomy defects all the same, so the server accepts a binding to one and the list offers them, and a person may
 * choose one.
 */
export const NON_PROCESS_IDS: ReadonlySet<string> = new Set(["machining_reject", "finishing_reject", "heat_treat_reject"]);

/** Said of a word the author matched to no shared defect: it starts at later, so it stays the plant's own category. */
export const NO_MATCH_NOTE = "No shared defect matches this word yet; it stays as the plant's own category.";

export function defectChoice(key: string): WordChoice {
  return `defect:${key}`;
}

/** The defect a choice names, or null for not a process defect, not scrap and later. */
export function defectOfChoice(c: WordChoice | null | undefined): string | null {
  return c && c.startsWith("defect:") && c.length > 7 ? c.slice(7) : null;
}

/** A choice in a few words, for a sentence: "Sand inclusion", "not a process defect", "not scrap", "decide later". One
 *  of the org's own defect types reads as its name, when the targets are known. */
export function choiceLabel(c: WordChoice, targets?: DefectTargets | null): string {
  const d = defectOfChoice(c);
  if (d) return targets ? targets.label(d) : defectLabel(d);
  if (c === OUT_OF_SCOPE) return "not a process defect (kept in scrap, out of process correlations)";
  if (c === NOT_SCRAP) return "not scrap (out of every scrap figure)";
  if (c === NEW_TYPE) return "a new defect type, not added yet";
  return "decide later";
}

export interface WordRow {
  word: string;
  /** The word as the server keys its binding (vocabKey). */
  key: string;
  note: string;
  /** The author's proposal as written: a defect, or null when no shared defect matches the word. */
  proposed: string | null;
  /** The choice the proposal makes: its defect folded onto the list; later when it names none, or one the list does
   *  not know. */
  proposal: WordChoice;
  /** The list is loaded and knows no defect the proposal names. */
  proposalUnknown: boolean;
  /** The proposal names scrap no process explains (NON_PROCESS_IDS): it makes not a process defect. */
  nonProcess: boolean;
  /** The org's approved decision on the word, when it already made one. */
  decided: WordChoice | null;
  /** When the org has not decided this spelling but decided the same word spelled another way (sameDefectWord), and
   *  every such decision agrees: that word as the org wrote it, and its decision. The row starts from it, so one
   *  defect is never split into two categories by a spelling. */
  spelledLike: { word: string; choice: WordChoice } | null;
  /** The word's spelling form (spellingForm): null when it has no word, or a letter outside ASCII. */
  form: string | null;
  /** The keys of the other words in this draft that are this word spelled another way. */
  twins: string[];
  /** Where the row starts: the org's decision, else the decision on its other spelling, else the proposal. */
  initial: WordChoice;
}

function choiceOfBinding(b: VocabBinding, targets: DefectTargets | null): WordChoice | null {
  if (b.scope === "out_of_scope") return OUT_OF_SCOPE;
  if (b.scope === "not_scrap") return NOT_SCRAP;
  const target = (b.target ?? "").trim();
  return target ? defectChoice(targets?.fold(target) ?? target) : null;
}

/**
 * A row per proposed word. It starts from the org's approved decision on the word when there is one (a person's
 * decision stands over the author's proposal), else from the proposal: its defect folded onto the list, or later.
 * Later when the proposal is null: a word no shared defect matches may still be a real defect the list lacks (a
 * plant's own word for gas holes), and unbound it stays on the outcome axis as the plant's category, where out of
 * scope would drop it from every process correlation. Later too when the list does not know the defect. Without the
 * list (still loading, or an older server) a proposed defect stands as its key, and the server checks it when saved.
 */
export function wordRows(
  proposals: readonly ProposedWord[],
  targets: DefectTargets | null,
  bindings?: readonly VocabBinding[] | null,
): WordRow[] {
  const decidedBy = new Map<string, WordChoice>();
  // Each word's spelling form, computed once (spellingForm), so a row's twins and inheritance cost no string work later.
  const decidedWords: { word: string; form: string | null; choice: WordChoice }[] = [];
  for (const b of bindings ?? []) {
    if (b.kind !== "defect" || b.status !== "approved") continue;
    const c = choiceOfBinding(b, targets);
    if (c) {
      decidedBy.set(b.source_key || vocabKey(b.source_name), c);
      decidedWords.push({ word: b.source_name, form: spellingForm(b.source_name), choice: c });
    }
  }
  const rows = proposals.map((p) => {
    const key = vocabKey(p.word);
    let proposal: WordChoice = LATER;
    let proposalUnknown = false;
    const nonProcess = p.taxonomyId !== null && NON_PROCESS_IDS.has(vocabKey(p.taxonomyId));
    if (nonProcess) {
      proposal = OUT_OF_SCOPE;
    } else if (p.taxonomyId !== null) {
      const folded = targets ? targets.fold(p.taxonomyId) : vocabKey(p.taxonomyId);
      proposal = folded ? defectChoice(folded) : LATER;
      proposalUnknown = folded === null;
    }
    const decided = decidedBy.get(key) ?? null;
    // The org's decisions on this word spelled other ways: a row starts from them only when they all agree.
    const form = spellingForm(p.word);
    const alike = decided === null ? decidedWords.filter((d) => sameSpelling(d.form, form)) : [];
    const spelledLike =
      alike.length > 0 && alike.every((d) => d.choice === alike[0].choice) ? { word: alike[0].word, choice: alike[0].choice } : null;
    return {
      word: p.word,
      key,
      note: p.note,
      proposed: p.taxonomyId,
      proposal,
      proposalUnknown,
      nonProcess,
      decided,
      spelledLike,
      form,
      twins: [] as string[],
      initial: decided ?? spelledLike?.choice ?? proposal,
    };
  });
  for (const r of rows) {
    r.twins = rows.filter((o) => o.key !== r.key && sameSpelling(o.form, r.form)).map((o) => o.key);
  }
  return rows;
}

/** Where a word starts when the org decided it under another spelling: "Spelled like “Sticker”, which the org
 *  decided as Sticker." "" otherwise. */
export function spellingText(row: WordRow): string {
  if (!row.spelledLike) return "";
  return `Spelled like “${row.spelledLike.word}”, which the org decided as ${choiceLabel(row.spelledLike.choice)}.`;
}

/**
 * What a word will mean once approved: the reviewer's choice, except that a word the org decided and the reviewer leaves
 * for later keeps the org's decision (nothing is written for it, and the decision stays in force).
 */
export function effectiveChoice(row: WordRow, choices: Readonly<Record<string, WordChoice>>): WordChoice {
  const c = choices[row.key] ?? row.initial;
  // A new type not added yet saves nothing either.
  return (c === LATER || c === NEW_TYPE) && row.decided !== null ? row.decided : c;
}

/**
 * When another spelling of this word in the same draft means something else under the reviewer's choices: a line
 * asking for one meaning, naming the spellings. "" when every spelling means the same.
 */
export function twinText(
  row: WordRow,
  rows: readonly WordRow[],
  choices: Readonly<Record<string, WordChoice>>,
): string {
  const here = effectiveChoice(row, choices);
  const differ = rows.filter((o) => row.twins.includes(o.key) && effectiveChoice(o, choices) !== here);
  if (differ.length === 0) return "";
  const names = differ.map((o) => `“${o.word}”`).join(", ");
  return `The same word as ${names} in this file, which means something else here: choose one meaning for both.`;
}

/** The org's decision on a word, when it made one: and, when the reviewer leaves the word for later, that the decision
 *  stays in force (nothing is written for it). "" for a word the org has not decided. */
export function decisionText(row: WordRow, current: WordChoice): string {
  if (row.decided === null) return "";
  const decided = `The org already decided: ${choiceLabel(row.decided)}.`;
  return current === LATER ? `${decided} Left for later, that decision stays in force.` : decided;
}

/** What the author proposed for a word, in a line. */
export function proposalText(row: WordRow): string {
  if (row.proposed === null) return NO_MATCH_NOTE;
  if (row.nonProcess) return `Proposed: ${row.proposed}, scrap no process explains: ${choiceLabel(OUT_OF_SCOPE)}`;
  if (row.proposalUnknown) return `Proposed: ${row.proposed}, which is not on the list of defects: choose one`;
  return `Proposed: ${choiceLabel(row.proposal)}`;
}

/** The defects a row's select offers: the list's, and any its choices name that the list does not, marked. */
export function defectOptions(
  row: WordRow,
  targets: DefectTargets | null,
  current: WordChoice,
): { value: WordChoice; label: string }[] {
  const keys = [...(targets?.keys ?? [])];
  const extra: string[] = [];
  for (const c of [current, row.decided, row.proposalUnknown ? null : row.proposal]) {
    const d = defectOfChoice(c);
    if (d && !keys.includes(d) && !extra.includes(d)) extra.push(d);
  }
  const marked = targets ? " (not on the list of defects)" : "";
  return [
    ...keys.map((k) => ({ value: defectChoice(k), label: defectOptionLabel(k, targets) })),
    ...extra.map((k) => ({ value: defectChoice(k), label: `${defectLabel(k)}${marked}` })),
  ];
}

/**
 * The bindings the reviewer's choices save, as approved: one per word with a decision, none for a word left for
 * later or decided as the org already had it. Each carries the author's note as its reason when the reviewer kept the
 * proposal, and what it rests on (this draft and its proposal) as evidence.
 */
export function wordBindings(
  rows: readonly WordRow[],
  choices: Readonly<Record<string, WordChoice>>,
  version: Pick<ImportExtractorVersion, "extractor_id" | "version">,
): VocabBindingInput[] {
  const out: VocabBindingInput[] = [];
  for (const r of rows) {
    const c = choices[r.key] ?? r.initial;
    if (c === LATER || c === r.decided) continue;
    const target = defectOfChoice(c);
    if (!target && c !== OUT_OF_SCOPE && c !== NOT_SCRAP) continue;
    const b: VocabBindingInput = {
      kind: "defect",
      source_name: r.word,
      scope: target ? "bound" : c === NOT_SCRAP ? "not_scrap" : "out_of_scope",
      status: "approved",
      evidence: { from: "extractor_review", extractor_id: version.extractor_id, version: version.version, proposed: r.proposed },
    };
    if (r.spelledLike && c === r.spelledLike.choice) b.evidence = { ...b.evidence, spelled_like: r.spelledLike.word };
    if (target) b.target = target;
    if (r.note && c === r.proposal) b.reason = r.note.slice(0, 2000);
    out.push(b);
  }
  return out;
}

/** What approving does with the words, in a line: "On approval 2 words are saved; 1 is left for later." */
export function wordsPlanText(rows: readonly WordRow[], choices: Readonly<Record<string, WordChoice>>, saved: number): string {
  // A decided word left for later, or decided again the same way, keeps the org's decision; nothing is written for it.
  // A new defect type not added yet is left for later too: nothing is saved for it.
  const isLater = (c: WordChoice) => c === LATER || c === NEW_TYPE;
  const keeps = (r: WordRow, c: WordChoice) => r.decided !== null && (c === r.decided || isLater(c));
  const later = rows.filter((r) => isLater(choices[r.key] ?? r.initial) && r.decided === null).length;
  const kept = rows.filter((r) => keeps(r, choices[r.key] ?? r.initial)).length;
  const parts = [saved ? `${plural(saved, "word")} ${saved === 1 ? "is" : "are"} saved as the org's` : "no word is saved"];
  if (kept) parts.push(`${plural(kept, "word")} ${kept === 1 ? "keeps its" : "keep their"} decision`);
  if (later) parts.push(`${plural(later, "word")} ${later === 1 ? "is" : "are"} left for later`);
  return `On approval ${parts.join("; ")}.`;
}

// ── Approving, then saving the words ───────────────────────────────────────────────────────────────────────────

/** Why a role cannot save words: the PUT takes model write, and the twin routes' write gate refuses a viewer first. */
export const ROLE_REFUSAL = "your role in this org may not change its words (that takes model write)";
/** Why an org without the foundry pack cannot: the vocabulary routes are the foundry pack's. */
export const PACK_REFUSAL = "this org has no foundry pack, which defect words belong to";

/** Why the words were not saved, in plain words; a refused binding is named by its word, not its index. */
export function wordsErrorText(err: unknown, words: readonly VocabBindingInput[] = []): string {
  const code = errorCodeOf(err);
  if (code === "insufficient_permission" || code === "insufficient_role") return ROLE_REFUSAL;
  if (code === "pack_not_enabled") return PACK_REFUSAL;
  if (isUnauthorized(err)) return "your session expired: sign in again";
  const detail = errorDetailOf(err);
  if (detail) {
    const m = /^bindings\[(\d+)\]:\s*/.exec(detail);
    const word = m ? words[Number(m[1])] : undefined;
    return m && word ? `${word.source_name}: ${detail.slice(m[0].length)}` : detail;
  }
  if (isNetworkError(err)) return "the server could not be reached";
  const status = statusOf(err);
  return status === undefined ? "the server refused them" : `the server answered HTTP ${status}`;
}

export type WordsResult = { ok: true; saved: number } | { ok: false; message: string };

/** Saves the words in one PUT (all or nothing on the server); none to save is a success. */
export async function saveWords(
  put: (bindings: VocabBindingInput[]) => Promise<unknown>,
  words: VocabBindingInput[],
): Promise<WordsResult> {
  if (words.length === 0) return { ok: true, saved: 0 };
  try {
    await put(words);
    return { ok: true, saved: words.length };
  } catch (err) {
    return { ok: false, message: wordsErrorText(err, words) };
  }
}

export type ApproveOutcome = { approved: false; error: unknown } | { approved: true; words: WordsResult };

/**
 * Approves the version, then saves the reviewer's words: in that order, so no word is saved for a version that was
 * not approved. When the words fail the approval stands: the caller says so, and nothing withdraws it.
 */
export async function approveWithWords(
  steps: { approve: () => Promise<unknown>; putWords: (bindings: VocabBindingInput[]) => Promise<unknown> },
  words: VocabBindingInput[],
): Promise<ApproveOutcome> {
  try {
    await steps.approve();
  } catch (error) {
    return { approved: false, error };
  }
  return { approved: true, words: await saveWords(steps.putWords, words) };
}

/** The approval, said once it went through: "Muller log v2 is in use: files waiting for it import now." */
export function approvedText(label: string, version: number, saved: number): string {
  const words = saved ? ` ${plural(saved, "defect word")} saved.` : "";
  return `${label} v${version} is in use: files waiting for it import now.${words}`;
}

/** The approval went through and the words did not: both said, plainly. */
export function wordsNotSavedText(label: string, version: number, message: string): string {
  return (
    `${label} v${version} is approved and in use: files waiting for it import now. ` +
    `Its defect words were not saved: ${message.replace(/[.\s]+$/, "")}. The approval stands.`
  );
}
